import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import type { ReactNode } from 'react';
import type {
  CMSData,
  CMSContextType,
  CMSSection,
  ServiceItem,
  TestimonialItem,
  CMSMobileOverrides,
} from '../types/cms';
import { defaultCMSData, defaultImages } from '../mocks/cmsData';
import { supabase } from '../lib/supabase';
import { useViewportBelowLg } from '../hooks/useViewportBelowLg';

const CMS_PWD_KEY = 'cms_admin_pwd';
const ADMIN_SESSION_KEY = 'cms_admin_session';
const DEFAULT_PASSWORD = 'nutricion2026';
const DEBOUNCE_MS = 800;

function encode(str: string): string {
  return btoa(encodeURIComponent(str));
}

function getStoredPassword(): string {
  return localStorage.getItem(CMS_PWD_KEY) ?? encode(DEFAULT_PASSWORD);
}

function mergeMobileFromStored(stored?: CMSMobileOverrides): CMSMobileOverrides {
  if (!stored || typeof stored !== 'object') return {};
  return {
    ...stored,
    services: stored.services
      ? {
          ...stored.services,
          items: { ...stored.services.items },
        }
      : undefined,
    testimonials: stored.testimonials
      ? {
          ...stored.testimonials,
          items: { ...stored.testimonials.items },
        }
      : undefined,
  };
}

function mergeData(stored: Partial<CMSData>, defaults: CMSData): CMSData {
  const mergedFooter = { ...defaults.footer, ...(stored.footer ?? {}) };
  const footerPhoneCompact = mergedFooter.phone.replace(/\s+/g, '');
  const normalizedEmail = mergedFooter.email.includes('carlosmendoza.com')
    ? 'dariorojasnutricion@gmail.com'
    : mergedFooter.email;
  const normalizedPhone =
    mergedFooter.phone.includes('+54') ||
    footerPhoneCompact === '685546738' ||
    footerPhoneCompact === '34685546738' ||
    footerPhoneCompact === '+34685546738'
      ? '+34 685 54 67 38'
      : mergedFooter.phone;
  const normalizedCopyright = mergedFooter.copyright.includes('Carlos Mendoza')
    ? '© 2026 Lic. Darío Rojas — Todos los derechos reservados'
    : mergedFooter.copyright;

  const mergedTestimonials = {
    ...defaults.testimonials,
    ...(stored.testimonials ?? {}),
    heading: stored.testimonials?.heading ?? defaults.testimonials.heading,
    items: stored.testimonials?.items?.length
      ? stored.testimonials.items
      : defaults.testimonials.items,
  };

  const mergedHero = {
    ...defaults.hero,
    ...(stored.hero ?? {}),
    secondaryCtaText: stored.hero?.secondaryCtaText ?? defaults.hero.secondaryCtaText,
    scrollHintText: stored.hero?.scrollHintText ?? defaults.hero.scrollHintText,
  };

  return {
    hero: mergedHero,
    profile: { ...defaults.profile, ...(stored.profile ?? {}) },
    services: {
      ...defaults.services,
      ...(stored.services ?? {}),
      items: stored.services?.items?.length ? stored.services.items : defaults.services.items,
    },
    testimonials: mergedTestimonials,
    contact: { ...defaults.contact, ...(stored.contact ?? {}) },
    calendly: { ...defaults.calendly, ...(stored.calendly ?? {}) },
    footer: {
      ...mergedFooter,
      email: normalizedEmail,
      phone: normalizedPhone,
      copyright: normalizedCopyright,
    },
    seo: { ...defaults.seo, ...(stored.seo ?? {}) },
    mobile: mergeMobileFromStored(stored.mobile),
  };
}

async function fetchFromSupabase<T>(key: string): Promise<T | null> {
  try {
    const { data, error } = await supabase
      .from('cms_content')
      .select('content_value')
      .eq('content_key', key)
      .maybeSingle();
    if (error || !data) return null;
    return data.content_value as T;
  } catch {
    return null;
  }
}

async function upsertToSupabase(key: string, value: unknown): Promise<void> {
  try {
    await supabase.from('cms_content').upsert(
      { content_key: key, content_value: value, updated_at: new Date().toISOString() },
      { onConflict: 'content_key' }
    );
  } catch {
    /* ignore */
  }
}

async function base64ToBlob(dataUrl: string): Promise<Blob> {
  const res = await fetch(dataUrl);
  return res.blob();
}

const CMSContext = createContext<CMSContextType | null>(null);

export function CMSProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<CMSData>(defaultCMSData);
  const [images, setImages] = useState<Record<string, string>>(defaultImages);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editViewport, setEditViewport] = useState<'desktop' | 'mobile'>('desktop');
  const [isAdmin, setIsAdmin] = useState(
    () => sessionStorage.getItem(ADMIN_SESSION_KEY) === 'true'
  );
  const [isLoading, setIsLoading] = useState(true);

  const viewportBelowLg = useViewportBelowLg();
  const isMobileContentActive =
    (!isEditMode && viewportBelowLg) || (isEditMode && editViewport === 'mobile');

  const dataDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const imagesDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!isEditMode) setEditViewport('desktop');
  }, [isEditMode]);

  useEffect(() => {
    let cancelled = false;
    async function loadData() {
      setIsLoading(true);
      try {
        const [storedData, storedImages] = await Promise.all([
          fetchFromSupabase<Partial<CMSData>>('data'),
          fetchFromSupabase<Record<string, string>>('images'),
        ]);
        if (cancelled) return;
        if (storedData) setData(mergeData(storedData, defaultCMSData));
        if (storedImages) setImages({ ...defaultImages, ...storedImages });
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          initializedRef.current = true;
        }
      }
    }
    loadData();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!initializedRef.current) return;
    if (dataDebounceRef.current) clearTimeout(dataDebounceRef.current);
    dataDebounceRef.current = setTimeout(() => {
      upsertToSupabase('data', data);
    }, DEBOUNCE_MS);
    return () => {
      if (dataDebounceRef.current) clearTimeout(dataDebounceRef.current);
    };
  }, [data]);

  useEffect(() => {
    if (!initializedRef.current) return;
    if (imagesDebounceRef.current) clearTimeout(imagesDebounceRef.current);
    imagesDebounceRef.current = setTimeout(() => {
      upsertToSupabase('images', images);
    }, DEBOUNCE_MS);
    return () => {
      if (imagesDebounceRef.current) clearTimeout(imagesDebounceRef.current);
    };
  }, [images]);

  const adminLogin = useCallback((password: string): boolean => {
    const stored = getStoredPassword();
    if (encode(password) === stored) {
      sessionStorage.setItem(ADMIN_SESSION_KEY, 'true');
      setIsAdmin(true);
      return true;
    }
    return false;
  }, []);

  const adminLogout = useCallback(() => {
    sessionStorage.removeItem(ADMIN_SESSION_KEY);
    setIsAdmin(false);
    setIsEditMode(false);
  }, []);

  const changeAdminPassword = useCallback((current: string, next: string): boolean => {
    if (encode(current) !== getStoredPassword()) return false;
    localStorage.setItem(CMS_PWD_KEY, encode(next));
    return true;
  }, []);

  const toggleEditMode = useCallback(() => {
    setIsEditMode((prev) => !prev);
  }, []);

  const updateField = useCallback(
    (section: CMSSection, field: string, value: string) => {
      setData((prev) => {
        const target = !isEditMode ? 'desktop' : editViewport;
        if (target === 'desktop') {
          return {
            ...prev,
            [section]: { ...(prev[section] as Record<string, unknown>), [field]: value },
          };
        }
        const prevMobileSection = (prev.mobile?.[section as keyof CMSMobileOverrides] ?? {}) as Record<
          string,
          unknown
        >;
        return {
          ...prev,
          mobile: {
            ...prev.mobile,
            [section]: { ...prevMobileSection, [field]: value },
          },
        };
      });
    },
    [isEditMode, editViewport]
  );

  const updateImage = useCallback(async (key: string, dataUrl: string) => {
    if (!dataUrl.startsWith('data:')) {
      setImages((prev) => ({ ...prev, [key]: dataUrl }));
      return;
    }

    try {
      const blob = await base64ToBlob(dataUrl);
      const ext = blob.type === 'image/png' ? 'png' : 'jpg';
      const fileName = `${key}_${Date.now()}.${ext}`;

      const { data: uploadData, error } = await supabase.storage
        .from('cms-images')
        .upload(fileName, blob, { upsert: true, contentType: blob.type });

      if (error || !uploadData) {
        throw new Error('No se pudo subir la imagen a Supabase Storage.');
      }

      const { data: publicData } = supabase.storage.from('cms-images').getPublicUrl(uploadData.path);

      const publicUrl = publicData.publicUrl;
      setImages((prev) => ({ ...prev, [key]: publicUrl }));
    } catch {
      throw new Error('No se pudo guardar la imagen en el bucket cms-images.');
    }
  }, []);

  const addService = useCallback(() => {
    const newService: ServiceItem = {
      id: `svc_${Date.now()}`,
      title: 'Nuevo Servicio',
      description: 'Descripción del servicio. Haz clic para editar.',
      duration: '60 minutos',
      price: '$0',
      featured: false,
    };
    setData((prev) => ({
      ...prev,
      services: { ...prev.services, items: [...prev.services.items, newService] },
    }));
  }, []);

  const removeService = useCallback((id: string) => {
    setData((prev) => ({
      ...prev,
      services: {
        ...prev.services,
        items: prev.services.items.filter((s) => s.id !== id),
      },
      mobile: {
        ...prev.mobile,
        services: prev.mobile.services
          ? {
              ...prev.mobile.services,
              items: Object.fromEntries(
                Object.entries(prev.mobile.services.items ?? {}).filter(([k]) => k !== id)
              ),
            }
          : undefined,
      },
    }));
  }, []);

  const updateService = useCallback(
    (id: string, field: keyof ServiceItem, value: string | boolean) => {
      setData((prev) => {
        if (!isEditMode || editViewport === 'desktop') {
          return {
            ...prev,
            services: {
              ...prev.services,
              items: prev.services.items.map((s) =>
                s.id === id ? { ...s, [field]: value } : s
              ),
            },
          };
        }
        const patch = {
          ...(prev.mobile.services?.items?.[id] ?? {}),
          [field]: value,
        };
        return {
          ...prev,
          mobile: {
            ...prev.mobile,
            services: {
              ...prev.mobile.services,
              items: {
                ...prev.mobile.services?.items,
                [id]: patch,
              },
            },
          },
        };
      });
    },
    [isEditMode, editViewport]
  );

  const addTestimonial = useCallback(() => {
    const newItem: TestimonialItem = {
      id: `tst_${Date.now()}`,
      text: 'Escribe aquí el testimonio del paciente.',
      name: 'Nombre del Paciente',
      role: 'Cargo o descripción',
      stars: 5,
    };
    setData((prev) => ({
      ...prev,
      testimonials: {
        ...prev.testimonials,
        items: [...prev.testimonials.items, newItem],
      },
    }));
  }, []);

  const removeTestimonial = useCallback((id: string) => {
    setData((prev) => ({
      ...prev,
      testimonials: {
        ...prev.testimonials,
        items: prev.testimonials.items.filter((t) => t.id !== id),
      },
      mobile: {
        ...prev.mobile,
        testimonials: prev.mobile.testimonials
          ? {
              ...prev.mobile.testimonials,
              items: Object.fromEntries(
                Object.entries(prev.mobile.testimonials.items ?? {}).filter(([k]) => k !== id)
              ),
            }
          : undefined,
      },
    }));
  }, []);

  const updateTestimonial = useCallback(
    (id: string, field: keyof TestimonialItem, value: string | number) => {
      setData((prev) => {
        if (!isEditMode || editViewport === 'desktop') {
          return {
            ...prev,
            testimonials: {
              ...prev.testimonials,
              items: prev.testimonials.items.map((t) =>
                t.id === id ? { ...t, [field]: value } : t
              ),
            },
          };
        }
        const patch = {
          ...(prev.mobile.testimonials?.items?.[id] ?? {}),
          [field]: value,
        };
        return {
          ...prev,
          mobile: {
            ...prev.mobile,
            testimonials: {
              ...prev.mobile.testimonials,
              items: {
                ...prev.mobile.testimonials?.items,
                [id]: patch,
              },
            },
          },
        };
      });
    },
    [isEditMode, editViewport]
  );

  const resetToDefaults = useCallback(() => {
    setData(defaultCMSData);
    setImages(defaultImages);
  }, []);

  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
        <div className="text-center">
          <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3">
            <i className="ri-loader-4-line text-3xl text-emerald-500 animate-spin"></i>
          </div>
          <p className="text-sm text-gray-500" style={{ fontFamily: 'Inter, sans-serif' }}>
            Cargando contenido...
          </p>
        </div>
      </div>
    );
  }

  return (
    <CMSContext.Provider
      value={{
        data,
        images,
        isEditMode,
        isAdmin,
        isMobileContentActive,
        editViewport,
        setEditViewport,
        toggleEditMode,
        adminLogin,
        adminLogout,
        changeAdminPassword,
        updateField,
        updateImage,
        addService,
        removeService,
        updateService,
        addTestimonial,
        removeTestimonial,
        updateTestimonial,
        resetToDefaults,
      }}
    >
      {children}
    </CMSContext.Provider>
  );
}

export function useCMS(): CMSContextType {
  const ctx = useContext(CMSContext);
  if (!ctx) throw new Error('useCMS must be used within CMSProvider');
  return ctx;
}
